import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useFormik } from 'formik';
import { useAppContext } from '../../../../store/AppContext';
import useModal from '../../../../utils/hooks/useModal';
import useNotification from '../../../../utils/hooks/useNotification';
import roleSchema from '../../../../validations/administration/roleSchema';

// Services
import useRolesService from '../../../../services/rolesService';

// Components
import FlashMessage from '../../../molecules/FlashMessage/FlashMessage';
import SelectField from '../../../atoms/SelectField/SelectField';
import InputField from '../../../atoms/InputField/InputField';

const RoleCreationEditionForm = forwardRef(({
  role, permissions, setRoles,
}, ref) => {
  const { t } = useTranslation();
  const [{ adminType }, stateDispatch] = useAppContext();

  const { closeModal } = useModal();
  const { displayNotification } = useNotification();

  const { postRole, putRole } = useRolesService();

  const [initialValues, setinitialValues] = useState({
    name: role?.name || '',
    permissions: null,
  });

  const permissionsOptions = useMemo(() => (permissions?.map((permission) => (
    { label: t(permission), value: permission }
  )) || []), [permissions]);

  useEffect(() => {
    if (permissionsOptions?.length && role?.permissions?.length) {
      setinitialValues((state) => ({
        ...state,
        permissions: permissionsOptions.filter((perm) => role.permissions.includes(perm.value)),
      }));
    }
  }, [permissionsOptions]);

  const { isLoading: postRoleLoading, mutate: postRoleMutate, error: postError } = useMutation(
    (values) => postRole({ type: adminType, role: values }),
    {
      onSuccess: (res) => {
        closeModal();
        if (res?.data) {
          setRoles((state) => state.concat([res.data]));
        }

        displayNotification({
          message: t('roles.creation.success', { name: res.data.name }),
        });
      },
    },
  );

  const { isLoading: putRoleLoading, mutate: putRoleMutate, error: putError } = useMutation(
    ({ roleId, values }) => putRole({ roleId, type: adminType, role: values }),
    {
      onSuccess: (res) => {
        closeModal();
        if (res?.data) {
          setRoles((state) => state.map((r) => (r.id === res.data.id ? res.data : r)));
        }

        displayNotification({
          message: t('roles.edition.success', { name: res.data.name }),
        });
      },
    },
  );

  useEffect(() => {
    if (postRoleLoading) {
      stateDispatch({ type: 'SET_MODAL_QUERY_LOADING', payload: 'postRole' });
    }

    if (putRoleLoading) {
      stateDispatch({ type: 'SET_MODAL_QUERY_LOADING', payload: 'putRole' });
    }

    if (!postRoleLoading && !putRoleLoading) {
      stateDispatch({ type: 'SET_MODAL_QUERY_LOADING', payload: null });
    }
  }, [postRoleLoading, putRoleLoading]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      const roleToSend = {
        name: values.name,
        permissions: values.permissions?.map((perm) => perm.value),
      };

      if (role?.id) {
        putRoleMutate({
          roleId: role.id,
          values: roleToSend,
        });
      } else {
        postRoleMutate(roleToSend);
      }
    },
    validationSchema: roleSchema,
  });

  useImperativeHandle(
    ref,
    () => ({
      submitForm: () => {
        formik.handleSubmit();
      },
    }),
  );

  const getInput = useCallback((name) => (
    <div className="m-b-15">
      <InputField
        id={`role_creation_${name}`}
        name={name}
        value={formik.values[name]}
        label={t(`roles.creation.form.${name}`)}
        formik={{
          handleChange: formik.handleChange,
          handleBlur: formik.handleBlur,
        }}
        error={formik.errors?.[name] && formik.touched?.[name] ? formik.errors[name] : null}
        isRequired
      />
    </div>
  ), [formik]);

  return (
    <form>
      {getInput('name')}

      {role?.permissions?.length && !formik.values?.permissions?.length ? null : (
        <SelectField
          id="roles_creation_permissions"
          name="permissions"
          options={permissionsOptions}
          label={t('roles.creation.form.permissions')}
          error={formik.errors?.permissions && formik.touched?.permissions ? formik.errors.permissions : null}
          setFieldTouched={formik.setFieldTouched}
          setFieldValue={formik.setFieldValue}
          value={formik.values.permissions}
          isRequired
          isMulti
        />
      )}

      {postError || putError ? (
        <div className="m-t-20">
          <FlashMessage
            color="error"
            message={t(`code_errors.${postError?.response?.data?.code || putError?.response?.data?.code || 'generic'}`)}
          />
        </div>
      ) : null}
    </form>
  );
});

RoleCreationEditionForm.propTypes = {
  role: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.string),
  }),
  permissions: PropTypes.arrayOf(PropTypes.string),
  setRoles: PropTypes.func,
};

RoleCreationEditionForm.defaultProps = {
  role: null,
  permissions: [],
  setRoles: () => {},
};

export default RoleCreationEditionForm;
